import * as XLSX from 'xlsx';

const EMPTY_STATS = {
  total: 0,
  success: 0,
  duplicate: 0,
  fail: 0
};
const HEADER_SCAN_ROW_LIMIT = 50;

function getEmptyScanResult() {
  return {
    rows: [],
    stats: { ...EMPTY_STATS },
    maxDepartmentDepth: 0
  };
}

function normalizeCellValue(value) {
  if (value === undefined || value === null) return '';
  return String(value).trim();
}

function isBlankCell(value) {
  return normalizeCellValue(value) === '';
}

function getCellValue(worksheet, rowIndex, columnIndex) {
  const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
  const cell = worksheet[cellAddress];
  if (!cell) return undefined;
  return cell.v !== undefined && cell.v !== null ? cell.v : cell.w;
}

function normalizeHeaderValue(value) {
  return normalizeCellValue(value).replace(/\s/g, '');
}

function isDepartmentHeaderCell(value) {
  return /^부서1($|\()/.test(normalizeHeaderValue(value));
}

function findScanConfig(worksheet, range) {
  const headerScanEndRow = Math.min(range.e.r, range.s.r + HEADER_SCAN_ROW_LIMIT - 1);

  for (let rowIndex = range.s.r; rowIndex <= headerScanEndRow; rowIndex += 1) {
    for (let columnIndex = range.s.c; columnIndex <= range.e.c; columnIndex += 1) {
      if (isDepartmentHeaderCell(getCellValue(worksheet, rowIndex, columnIndex))) {
        return {
          startRow: rowIndex + 1,
          startColumn: columnIndex
        };
      }
    }
  }

  return {
    startRow: range.s.r,
    startColumn: range.s.c
  };
}

function createDivisionRow(departments, rowIndex) {
  const row = {
    rowNumber: rowIndex + 1,
    departments
  };

  departments.forEach((department, index) => {
    row[`dept${index + 1}`] = department;
  });

  return row;
}

function scanDivisionRowsFromWorksheet(worksheet) {
  if (!worksheet || !worksheet['!ref']) return getEmptyScanResult();

  const range = XLSX.utils.decode_range(worksheet['!ref']);
  const rows = [];
  let maxDepartmentDepth = 0;
  const { startRow, startColumn } = findScanConfig(worksheet, range);

  for (let rowIndex = startRow; rowIndex <= range.e.r; rowIndex += 1) {
    if (isBlankCell(getCellValue(worksheet, rowIndex, startColumn))) break;

    const departments = [];

    for (let columnIndex = startColumn; columnIndex <= range.e.c; columnIndex += 1) {
      const cellValue = normalizeCellValue(getCellValue(worksheet, rowIndex, columnIndex));
      if (cellValue === '') break;
      departments.push(cellValue);
    }

    if (departments.length > 0) {
      maxDepartmentDepth = Math.max(maxDepartmentDepth, departments.length);
      rows.push(createDivisionRow(departments, rowIndex));
    }
  }

  return {
    rows,
    stats: {
      ...EMPTY_STATS,
      total: rows.length
    },
    maxDepartmentDepth
  };
}

function scanDivisionRowsFromWorkbook(workbook) {
  if (!workbook || !workbook.SheetNames || workbook.SheetNames.length === 0) {
    return getEmptyScanResult();
  }

  const firstSheetName = workbook.SheetNames[0];
  return scanDivisionRowsFromWorksheet(workbook.Sheets[firstSheetName]);
}

function readDivisionExcelFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = event.target.result;
        const workbook = XLSX.read(data, { type: 'array' });
        resolve(scanDivisionRowsFromWorkbook(workbook));
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

export default {
  scanDivisionRowsFromWorksheet,
  scanDivisionRowsFromWorkbook,
  readDivisionExcelFile,
  isBlankCell
};
